import { useState, useMemo, useEffect } from 'react';
import {
    Typography,
    TextField,
    Grid,
    IconButton,
    Paper,
    Tooltip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import Styles from './CreateCaseStudy.module.scss';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import { InfoOutlined } from '@mui/icons-material';

export function CaseStudyForm({ caseStudyData, onChange, isSubComponent = false }) {
    const initialFormState = {
        name: '',
        description: '',
        scenario: '',
        question: '',
        response_template: {
            ideal: '',
            average: '',
            poor: ''
        },
        currentKeyword: '',
        criteria: [],
    };

    const [formData, setFormData] = useState(initialFormState);
    const [editingCriteriaIndex, setEditingCriteriaIndex] = useState(null);
    const [editCriteriaOpen, setEditCriteriaOpen] = useState(false);
    const [editingCriteria, setEditingCriteria] = useState({
        name: '',
        description: '',
        weightage: ''
    });

    const [touched, setTouched] = useState({
        scenario: false,
        question: false,
        'response_template.ideal': false,
        'response_template.average': false,
        'response_template.poor': false
    });

    const [newCriteria, setNewCriteria] = useState({
        name: '',
        description: '',
        weightage: ''
    });

    useEffect(() => {
        if (caseStudyData) {
            setFormData(caseStudyData);
        }
    }, [caseStudyData]);

    // Error validation for each field
    const errors = useMemo(() => ({
        scenario: touched.scenario && !formData.scenario.trim() ? 'Scenario is required' : '',
        question: touched.question && !formData.question?.trim() ? 'Question is required' : '',
        'response_template.ideal': touched['response_template.ideal'] && !formData.response_template.ideal.trim() ? 'Ideal criteria is required' : '',
        'response_template.average': touched['response_template.average'] && !formData.response_template.average.trim() ? 'Average criteria is required' : '',
        'response_template.poor': touched['response_template.poor'] && !formData.response_template.poor.trim() ? 'Poor criteria is required' : '',
        criteria: formData.criteria.length === 0 ? 'At least one criteria is required' : ''
    }), [formData, touched]);

    const handleBlur = (fieldName) => {
        setTouched(prev => ({
            ...prev,
            [fieldName]: true
        }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const newFormData = { ...formData };

        if (name.startsWith('response_template.')) {
            const templateField = name.split('.')[1];
            newFormData.response_template = {
                ...newFormData.response_template,
                [templateField]: value
            };
        } else {
            newFormData[name] = value;
        }

        setFormData(newFormData);
        onChange && onChange(newFormData);
    };

    // Calculate remaining weightage (excluding a specific criteria if editing)
    const getRemainingWeightage = (excludeIndex = -1) => {
        const currentTotal = formData.criteria.reduce(
            (sum, criteria, index) => {
                if (index === excludeIndex) return sum;
                return sum + parseFloat(criteria.weightage || 0);
            },
            0
        );
        return 100 - currentTotal;
    };

    const getCriteriaWeightageError = () => {
        if (!newCriteria.weightage) return '';
        const weightage = parseFloat(newCriteria.weightage);
        if (isNaN(weightage)) return 'Please enter a valid number';
        if (weightage <= 0) return 'Weightage must be greater than 0';
        if (weightage > getRemainingWeightage()) return `Exceeds available weightage of ${getRemainingWeightage()}%`;
        return '';
    };

    const getEditCriteriaWeightageError = () => {
        if (!editingCriteria.weightage) return '';
        const weightage = parseFloat(editingCriteria.weightage);
        if (isNaN(weightage)) return 'Please enter a valid number';
        if (weightage <= 0) return 'Weightage must be greater than 0';

        // Available weightage is the remaining (excluding current) + the original value of current criteria
        const availableWeightage = getRemainingWeightage(editingCriteriaIndex);

        if (weightage > availableWeightage)
            return `Exceeds available weightage of ${availableWeightage}%`;
        return '';
    };

    const handleCriteriaInputChange = (e) => {
        const { name, value } = e.target;
        setNewCriteria(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleEditCriteriaInputChange = (e) => {
        const { name, value } = e.target;
        setEditingCriteria(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleAddCriteria = () => {
        if (newCriteria.name.trim() &&
            newCriteria.weightage.trim() &&
            !getCriteriaWeightageError()) {

            const updatedFormData = {
                ...formData,
                criteria: [...formData.criteria, {
                    ...newCriteria,
                    weightage: parseFloat(newCriteria.weightage)
                }]
            };

            setFormData(updatedFormData);
            onChange && onChange(updatedFormData);

            setNewCriteria({
                name: '',
                description: '',
                weightage: ''
            });
        }
    };

    const handleDeleteCriteria = (indexToRemove) => {
        const updatedFormData = {
            ...formData,
            criteria: formData.criteria.filter((_, index) => index !== indexToRemove)
        };
        setFormData(updatedFormData);
        onChange && onChange(updatedFormData);
    };

    const handleOpenEditCriteria = (index) => {
        setEditingCriteriaIndex(index);
        setEditingCriteria({
            name: formData.criteria[index].name,
            description: formData.criteria[index].description,
            weightage: formData.criteria[index].weightage.toString()
        });
        setEditCriteriaOpen(true);
    };

    const handleCloseEditCriteria = () => {
        setEditCriteriaOpen(false);
        setEditingCriteriaIndex(null);
    };

    const handleSaveEditCriteria = () => {
        if (editingCriteriaIndex !== null &&
            editingCriteria.name.trim() &&
            editingCriteria.weightage.trim() &&
            !getEditCriteriaWeightageError()) {

            const updatedCriteria = [...formData.criteria];
            updatedCriteria[editingCriteriaIndex] = {
                ...editingCriteria,
                weightage: parseFloat(editingCriteria.weightage)
            };

            const updatedFormData = {
                ...formData,
                criteria: updatedCriteria
            };

            setFormData(updatedFormData);
            onChange && onChange(updatedFormData);
            handleCloseEditCriteria();
        }
    };

    return (
        <div className={Styles.caseStudyCreate}>
            <div className={Styles.caseStudyForm}>
                <TextField
                    fullWidth
                    required
                    label="Case Study Scenario"
                    name="scenario"
                    value={formData.scenario}
                    onChange={handleInputChange}
                    onBlur={() => handleBlur('scenario')}
                    error={!!errors.scenario}
                    helperText={errors.scenario}
                    multiline
                    rows={4}
                    variant='filled'
                    sx={{
                        '& .MuiFilledInput-root': {
                            backgroundColor: '#f9f9f9',
                        },
                    }}
                />

                <TextField
                    fullWidth
                    required
                    label="Question"
                    name="question"
                    value={formData.question || ''}
                    onChange={handleInputChange}
                    onBlur={() => handleBlur('question')}
                    error={!!errors.question}
                    helperText={errors.question}
                    variant='filled'
                    sx={{
                        '& .MuiFilledInput-root': {
                            backgroundColor: '#f9f9f9',
                        },
                    }}
                />

                <Typography variant="h6" gutterBottom>Template Answers</Typography>
                <div style={{ display: 'flex', gap: '1em' }}>
                    <TextField
                        fullWidth
                        required
                        label="Ideal Answer Template"
                        name="response_template.ideal"
                        value={formData.response_template.ideal}
                        onChange={handleInputChange}
                        onBlur={() => handleBlur('response_template.ideal')}
                        error={!!errors['response_template.ideal']}
                        helperText={errors['response_template.ideal']}
                        multiline
                        rows={8}
                        variant='filled'
                        sx={{
                            '& .MuiFilledInput-root': {
                                backgroundColor: '#f9f9f9',
                            },
                        }}
                    />
                    <TextField
                        fullWidth
                        required
                        label="Average Answer Template"
                        name="response_template.average"
                        value={formData.response_template.average}
                        onChange={handleInputChange}
                        onBlur={() => handleBlur('response_template.average')}
                        error={!!errors['response_template.average']}
                        helperText={errors['response_template.average']}
                        multiline
                        rows={8}
                        variant='filled'
                        sx={{
                            '& .MuiFilledInput-root': {
                                backgroundColor: '#f9f9f9',
                            },
                        }}
                    />
                    <TextField
                        fullWidth
                        required
                        label="Poor Answer Template"
                        name="response_template.poor"
                        value={formData.response_template.poor}
                        onChange={handleInputChange}
                        onBlur={() => handleBlur('response_template.poor')}
                        error={!!errors['response_template.poor']}
                        helperText={errors['response_template.poor']}
                        multiline
                        rows={8}
                        variant='filled'
                        sx={{
                            '& .MuiFilledInput-root': {
                                backgroundColor: '#f9f9f9',
                            },
                        }}
                    />
                </div>

                <Typography variant="h6" gutterBottom>Evaluation Criteria</Typography>
                <Grid container spacing={2} className={Styles.criteriaInputRow}>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            required
                            fullWidth
                            label="Criteria Name"
                            name="name"
                            value={newCriteria.name}
                            onChange={handleCriteriaInputChange}
                            helperText={' '}
                            variant='filled'
                            sx={{
                                '& .MuiFilledInput-root': {
                                    backgroundColor: '#f9f9f9',
                                },
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            label="Criteria Description"
                            name="description"
                            value={newCriteria.description}
                            onChange={handleCriteriaInputChange}
                            helperText={' '}
                            variant='filled'
                            sx={{
                                '& .MuiFilledInput-root': {
                                    backgroundColor: '#f9f9f9',
                                },
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            fullWidth
                            required
                            label="Weightage (%)"
                            name="weightage"
                            type="number"
                            value={newCriteria.weightage}
                            onChange={handleCriteriaInputChange}
                            error={!!getCriteriaWeightageError()}
                            helperText={
                                <span style={{ display: 'flex', alignItems: 'center' }}>
                                    {`Remaining: ${getRemainingWeightage()}%`}
                                    <Tooltip title="Sum of criteria weightage should be 100%" arrow>
                                        <InfoOutlined fontSize="small" sx={{ ml: 1, cursor: "pointer" }} />
                                    </Tooltip>
                                </span>
                            }
                            variant='filled'
                            sx={{
                                '& .MuiFilledInput-root': {
                                    backgroundColor: '#f9f9f9',
                                },
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={1}>
                        <IconButton
                            color="primary"
                            onClick={handleAddCriteria}
                            disabled={!newCriteria.name.trim() ||
                                !newCriteria.weightage.trim() ||
                                !!getCriteriaWeightageError()}
                        >
                            <AddCircleOutlineIcon />
                        </IconButton>
                    </Grid>
                </Grid>

                {formData.criteria.length > 0 && (
                    <Paper className={Styles.criteriaListContainer}>
                        <Typography variant="h6" gutterBottom>Added Criteria</Typography>
                        {formData.criteria.map((criteria, index) => (
                            <Grid container spacing={2} key={index} className={Styles.criteriaListItem}>
                                <Grid item xs={4}>
                                    <Typography>{criteria.name}</Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography>{criteria.description}</Typography>
                                </Grid>
                                <Grid item xs={3}>
                                    <Typography>{criteria.weightage}%</Typography>
                                </Grid>
                                <Grid item xs={1}>
                                    <div style={{ display: 'flex' }}>
                                        <IconButton
                                            onClick={() => handleOpenEditCriteria(index)}
                                            color="primary"
                                            size="small"
                                            sx={{ color: '#666' }}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            onClick={() => handleDeleteCriteria(index)}
                                            color="secondary"
                                            size="small"
                                            sx={{ color: 'red' }}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </div>
                                </Grid>
                            </Grid>
                        ))}
                        <Typography variant="body2" color="textSecondary" align="right" style={{ marginTop: '10px' }}>
                            Total Weightage: {formData.criteria.reduce((sum, item) => sum + parseFloat(item.weightage || 0), 0)}%
                        </Typography>
                    </Paper>
                )}
            </div>

            {/* Edit Criteria Dialog */}
            <Dialog open={editCriteriaOpen} onClose={handleCloseEditCriteria}>
                <DialogTitle>Edit Criteria</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} style={{ marginTop: '8px' }}>
                        <Grid item xs={12}>
                            <TextField
                                required
                                fullWidth
                                label="Criteria Name"
                                name="name"
                                value={editingCriteria.name}
                                onChange={handleEditCriteriaInputChange}
                                variant='filled'
                                sx={{
                                    '& .MuiFilledInput-root': {
                                        backgroundColor: '#f9f9f9',
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Criteria Description"
                                name="description"
                                value={editingCriteria.description}
                                onChange={handleEditCriteriaInputChange}
                                variant='filled'
                                sx={{
                                    '& .MuiFilledInput-root': {
                                        backgroundColor: '#f9f9f9',
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                required
                                label="Weightage (%)"
                                name="weightage"
                                type="number"
                                value={editingCriteria.weightage}
                                onChange={handleEditCriteriaInputChange}
                                error={!!getEditCriteriaWeightageError()}
                                helperText={
                                    getEditCriteriaWeightageError() ||
                                    `Available weightage: ${getRemainingWeightage(editingCriteriaIndex)}%`
                                }
                                variant='filled'
                                sx={{
                                    '& .MuiFilledInput-root': {
                                        backgroundColor: '#f9f9f9',
                                    },
                                }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseEditCriteria}>Cancel</Button>
                    <Button
                        onClick={handleSaveEditCriteria}
                        color="primary"
                        disabled={!editingCriteria.name.trim() ||
                            !editingCriteria.weightage.trim() ||
                            !!getEditCriteriaWeightageError()}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}